import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useShares } from '../contexts/SharesContext';
import { useProjects } from '../hooks/useProjects';
import { Plus, FileText, Image, Trash2, Search, X, Edit2, Hash, Users } from 'lucide-react';
import { DropdownMenu, DropdownItem } from '../components/DropdownMenu';
import { ProjectCodeModal } from '../components/ProjectCodeModal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { toast } from '../lib/toast';
import { timeAgo } from '../lib/dates';
import { DashboardSkeleton, ProjectCardSkeleton } from '../components/LoadingShimmer';
import DashboardOnboarding from '../components/DashboardOnboarding';
import { getProjectColor } from '../lib/colors';
import * as projectService from '../services/project.service';
import type { ProjectWithCount, Project } from '../services/project.service';
import * as customizationService from '../services/customization.service';
import { ImageUpload } from '../components/ImageUpload';

import { CollaboratorIndicator } from '../components/CollaboratorIndicator';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Header } from '../components/Header';
import { CustomizationModal } from '../components/CustomizationModal';
import { EditProjectModal } from '../components/EditProjectModal';
import { CreateProjectModal } from '../components/CreateProjectModal';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { usePageTitle } from '../hooks/usePageTitle';

export default function Dashboard() {
  usePageTitle('Projetos');
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { user, logout } = useAuth();
  const { loadSharesBulk } = useShares();
  const isColaborador = user?.account_type === 'colaborador';
  const { data: projects = [], isLoading: loading, error, refetch } = useProjects();
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [editingProject, setEditingProject] = useState<string | null>(null);
  const [editProjectName, setEditProjectName] = useState('');
  const [editProjectDesc, setEditProjectDesc] = useState('');

  const [creatingProjects, setCreatingProjects] = useState(0);
  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [headerImageUrl, setHeaderImageUrl] = useState<string | null>(null);
  const [defaultTopics, setDefaultTopics] = useState<string[]>([]);
  const [defaultCompany, setDefaultCompany] = useState<string | null>(null);
  const [changingCoverProject, setChangingCoverProject] = useState<Project | null>(null);
  const [newCoverImage, setNewCoverImage] = useState<string | null>(null);
  const [savingCover, setSavingCover] = useState(false);
  const [editingCodeProject, setEditingCodeProject] = useState<Project | null>(null);


  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetchCustomization();
    checkOnboarding();
  }, [user, navigate]);

  useEffect(() => {
    if (projects && projects.length > 0) {
      const projectIds = projects.map(p => Number(p.id));
      loadSharesBulk('project', projectIds);
    }
  }, [projects, loadSharesBulk]);

  const fetchCustomization = async () => {
    try {
      const customization = await customizationService.getUserCustomization();
      setHeaderImageUrl(customization.header_image_url);
      setDefaultTopics(customization.default_topics || []);
      setDefaultCompany(customization.default_company);
    } catch (error) {
      console.error('Erro ao carregar personalização:', error);
    }
  };

  const checkOnboarding = async () => {
    try {

      if (localStorage.getItem('e2e-test-mode') === 'true') {
        return;
      }


      const storageKey = 'dashboard_onboarding_disabled';
      if (localStorage.getItem(storageKey) === 'true') {
        return;
      }


      const sessionKey = 'dashboard_onboarding_shown';
      if (sessionStorage.getItem(sessionKey) === 'true') {
        return;
      }


      if (user) {
        setShowOnboarding(true);
        sessionStorage.setItem(sessionKey, 'true');
      }
    } catch (error) {
      console.error('Error checking onboarding:', error);
    }
  };

  const handleCloseOnboarding = () => {
    setShowOnboarding(false);
  };

  const handleDontShowAgain = async () => {

    localStorage.setItem('dashboard_onboarding_disabled', 'true');
    setShowOnboarding(false);
  };


  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleOpenCustomization = () => {
    setShowCustomizationModal(true);
  };

  const handleCloseCustomization = () => {
    setShowCustomizationModal(false);
    fetchCustomization();
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      await projectService.deleteProject(projectId);
      toast.success(t('dashboard.project_deleted'));
      await refetch();
      setDeleteConfirm(null);
    } catch (error: any) {
      console.error('Error:', error);
      const errMsg = error.message || '';
      if (errMsg.includes('conexão') || errMsg.includes('internet')) {
        toast.error('Não foi possível excluir o projeto — verifique sua conexão com a internet.');
      } else if (errMsg.includes('permissão') || errMsg.includes('403')) {
        toast.error('Você não tem permissão para excluir este projeto.');
      } else {
        toast.error(errMsg || 'Não foi possível excluir o projeto. Tente novamente.');
      }

    }
  };

  const handleChangeCover = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setChangingCoverProject(project);
      setNewCoverImage(project.cover_image || null);
    }
  };

  const handleSaveCoverImage = async () => {
    if (!changingCoverProject) {
      return;
    }


    if (!newCoverImage) {
      toast.error(t('project.select_image_first'));
      return;
    }

    try {
      setSavingCover(true);

      await projectService.updateProject(changingCoverProject.id, {
        cover_image: newCoverImage,
      });

      // Refetch projects para atualizar o cache do React Query
      await refetch();

      toast.success(t('dashboard.cover_updated'));
      setChangingCoverProject(null);
      setNewCoverImage(null);
    } catch (error: any) {
      console.error('Erro ao atualizar imagem:', error);
      const errMsg = error.message || '';
      if (errMsg.includes('conexão') || errMsg.includes('internet')) {
        toast.error('Não foi possível alterar a imagem — verifique sua conexão com a internet.');
      } else if (errMsg.includes('grande') || errMsg.includes('size')) {
        toast.error('Imagem muito grande. O tamanho máximo é 5MB.');
      } else {
        toast.error(errMsg || 'Não foi possível atualizar a imagem de capa. Tente novamente.');
      }
    } finally {
      setSavingCover(false);
    }
  };

  const handleCloseCoverModal = () => {
    setChangingCoverProject(null);
    setNewCoverImage(null);
  };

  const handleEditProject = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setEditingProject(projectId);
      setEditProjectName(project.name);
      setEditProjectDesc(project.description || '');
    }
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setEditProjectName('');
    setEditProjectDesc('');
  };



  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    toast.error(t('dashboard.error_loading'));
  }

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Header
        breadcrumbs={[{ label: t('dashboard.title') }]}
        onCustomizationClick={handleOpenCustomization}
      />

      {/* Barra secundária */}
      <div className="bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            {/* Info principal */}
            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-lg font-semibold text-foreground truncate">
                  {isColaborador ? t('sharedWithMe.title') : t('dashboard.title')}
                </h1>
                {projects.length > 0 && (
                  <>
                    <span className="text-muted-foreground hidden sm:inline">•</span>
                    <span className="text-sm text-muted-foreground hidden sm:inline">
                      {t('dashboard.projects_count', { count: projects.length })}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Busca */}
            {projects.length > 0 && (
              <div className="relative w-48 sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10" size={16} />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('dashboard.search_placeholder')}
                  className="pl-9 pr-8 h-9 text-sm"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors z-10"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )}

            {/* Ações */}
            {user?.account_type !== 'colaborador' && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => navigate('/equipe')}
                  className="flex items-center gap-2 px-3 py-2 text-sm border border-border bg-background text-foreground rounded-md hover:bg-muted transition-colors shadow-sm"
                >
                  <Users size={16} />
                  <span className="hidden sm:inline">Equipe</span>
                </button>
                {projects.length > 0 && (
                  <button
                    onClick={() => setShowModal(true)}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors shadow-sm"
                  >
                    <Plus size={16} />
                    <span className="hidden sm:inline">{t('dashboard.create_project')}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-6 pb-24">

        {projects.length === 0 && creatingProjects === 0 ? (
          <Card className="text-center py-20">
            <CardContent className="pt-6">
              <FileText size={64} className="mx-auto text-gray-300 mb-4" />
              <CardTitle className="text-xl mb-2">{t('dashboard.no_projects')}</CardTitle>
              <CardDescription className="mb-6">{t('dashboard.create_first_project')}</CardDescription>
              {user?.account_type !== 'colaborador' && (
                <button
                  onClick={() => setShowModal(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-[#013EFB] text-white hover:bg-[#012BC8] transition-all btn-scale shadow-sm mx-auto"
                >
                  <Plus size={20} />
                  {t('dashboard.create_project')}
                </button>
              )}
            </CardContent>
          </Card>
        ) : projects.length === 0 && creatingProjects > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: creatingProjects }).map((_, index) => (
              <ProjectCardSkeleton key={`creating-first-${index}`} />
            ))}
          </div>
        ) : filteredProjects.length === 0 ? (
          <Card className="text-center py-20">
            <CardContent className="pt-6">
              <Search size={64} className="mx-auto text-gray-300 mb-4" />
              <CardTitle className="text-xl mb-2">{t('dashboard.no_results')}</CardTitle>
              <CardDescription className="mb-6">{t('dashboard.try_other_terms')}</CardDescription>
              <button
                onClick={() => setSearchQuery('')}
                className="px-6 py-3 bg-[#013EFB] text-white hover:bg-[#012BC8] transition-all btn-scale"
              >
                {t('clear_search')}
              </button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {}
            {Array.from({ length: creatingProjects }).map((_, index) => (
              <ProjectCardSkeleton key={`creating-${index}`} />
            ))}
            {filteredProjects.map((project) => (
              <Card
                key={project.id}
                className="group hover:border-[#013EFB] transition-all overflow-hidden fade-in relative"
              >
                {isColaborador && (
                  <div className="absolute top-3 left-3 z-10">
                    <Badge variant={project.permission === 'edit' ? 'default' : 'secondary'} className="gap-1">
                      {project.permission === 'edit' ? t('sharedWithMe.can_edit') : t('sharedWithMe.view_only')}
                    </Badge>
                  </div>
                )}
                {(!isColaborador || project.permission === 'edit') && (
                <div className="absolute top-3 right-3 z-10">
                  <DropdownMenu>
                    <DropdownItem
                      onClick={() => handleEditProject(project.id)}
                      icon={<Edit2 size={16} />}
                    >
                      {t('project.edit_name')}
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => setEditingCodeProject(project)}
                      icon={<Hash size={16} />}
                    >
                      Código da obra
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => handleChangeCover(project.id)}
                      icon={<Image size={16} />}
                    >
                      {t('project.change_cover')}
                    </DropdownItem>
                    {!isColaborador && (
                      <DropdownItem
                        onClick={() => setDeleteConfirm(project.id)}
                        icon={<Trash2 size={16} />}
                        danger
                      >
                        {t('project.delete')}
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                </div>
                )}
                <div
                  onClick={() => navigate(`/projects/${project.id}/meetings`)}
                  className="cursor-pointer"
                >
                  <div
                    className="relative h-32 bg-gradient-to-br transition-all bg-cover bg-center"
                    style={project.cover_image ?
                      { backgroundImage: `url(${project.cover_image})` } :
                      {
                        background: `linear-gradient(to bottom right, ${getProjectColor(project.id).from}, ${getProjectColor(project.id).to})`
                      }
                    }
                  >
                    {project.cover_image && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                    )}
                  </div>
                  <CardHeader className="p-6 space-y-0">
                    <CardTitle className="text-lg font-semibold text-foreground mb-2 line-clamp-1">
                      {project.name}
                    </CardTitle>
                    {project.description && (
                      <CardDescription className="text-muted-foreground mb-4 line-clamp-2">
                        {project.description}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardFooter className="flex items-center gap-3 text-sm text-muted-foreground pt-4 border-t border-border">
                    <div className="flex items-center gap-1">
                      <FileText size={14} />
                      <span>{t('dashboard.meetings_count', { count: project.meetingCount || 0 })}</span>
                    </div>
                    {project.last_meeting_date && (
                      <span className="text-xs">• {timeAgo(project.last_meeting_date)}</span>
                    )}
                    <CollaboratorIndicator
                      resourceType="project"
                      resourceId={Number(project.id)}
                    />
                  </CardFooter>
                </div>
              </Card>
            ))}
          </div>
        )}



      </main>

      {deleteConfirm && (
        <ConfirmDialog
          title={t('dashboard.delete_project_title')}
          message={t('dashboard.delete_project_message')}
          confirmText={t('delete')}
          cancelText={t('cancel')}
          onConfirm={() => handleDeleteProject(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
          type="danger"
        />
      )}

      {}
      <EditProjectModal
        open={Boolean(editingProject)}
        onClose={handleCancelEdit}
        projectName={editProjectName}
        projectDescription={editProjectDesc}
        onSave={async (name, description) => {
          if (!editingProject) return;
          await projectService.updateProject(editingProject, {
            name,
            description: description || undefined,
          });
          await refetch();
          setEditingProject(null);
          setEditProjectName('');
          setEditProjectDesc('');
        }}
      />

      <ProjectCodeModal
        open={Boolean(editingCodeProject)}
        onClose={() => setEditingCodeProject(null)}
        projectName={editingCodeProject?.name || ''}
        currentCode={editingCodeProject?.project_code || ''}
        onSave={async (code) => {
          if (!editingCodeProject) return;
          await projectService.updateProject(editingCodeProject.id, {
            project_code: code.trim() || null,
          });
          await refetch();
          setEditingCodeProject(null);
          toast.success('Código da obra atualizado');
        }}
      />


      <CreateProjectModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSave={async (name, description, imageUrl) => {

          setCreatingProjects(prev => prev + 1);

          try {
            const project = await projectService.createProject({
              name,
              description: description || undefined,
              cover_image: imageUrl || undefined,
            });


            if (!project || !project.id) {
              throw new Error('Projeto criado mas resposta inválida');
            }

            await refetch();
            toast.success(t('dashboard.project_created'));
          } catch (error: any) {
            console.error('Erro ao criar projeto:', error);
            const errorMessage = error?.response?.data?.message
              || error?.message
              || 'Erro desconhecido ao criar projeto';
            toast.error(errorMessage);
            throw error;
          } finally {

            setCreatingProjects(prev => Math.max(0, prev - 1));
          }
        }}
      />



      {showOnboarding && (
        <DashboardOnboarding
          onClose={handleCloseOnboarding}
          onDontShowAgain={handleDontShowAgain}
        />
      )}

      {}
      <CustomizationModal
        open={showCustomizationModal}
        onClose={handleCloseCustomization}
        currentImage={headerImageUrl}
        defaultTopics={defaultTopics}
        defaultCompany={defaultCompany}
        onSave={async (imageUrl, topics, company) => {
          await customizationService.updateUserCustomization(imageUrl, topics, company);
          await fetchCustomization();
          setDefaultTopics(topics);
          setDefaultCompany(company);
          toast.success(t('dashboard.customization_saved'));
        }}
      />

      {}
      {changingCoverProject && (
        <Dialog open={Boolean(changingCoverProject)} onOpenChange={handleCloseCoverModal}>
          <DialogContent className="max-w-md">
            <DialogHeader className="space-y-1.5">
              <DialogTitle className="text-lg flex items-center gap-2">
                <Image size={18} className="text-primary" />
                {t('project.update_cover_title')}
              </DialogTitle>
              <DialogDescription className="text-sm truncate">
                {t('project.update_cover_description', { name: changingCoverProject.name })}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-1.5 pt-2">
              <Label className="text-sm">{t('project.cover_image')}</Label>
              <ImageUpload
                currentImage={newCoverImage}
                onImageChange={setNewCoverImage}
                disabled={savingCover}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleCloseCoverModal} disabled={savingCover} size="sm">
                {t('cancel')}
              </Button>
              <Button
                onClick={handleSaveCoverImage}
                disabled={savingCover || !newCoverImage}
                size="sm"
              >
                {savingCover ? t('saving') : t('save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
